/**
 * Derives "artifacts", the files an agent wrote during a session, from the tool
 * calls in its transcript. There is no platform-level artifact feed yet (see
 * github/copilot-experiences#12895).
 *
 * Transcripts reach the UI as AHP `ChatState`; each surface contributes a thin
 * projection into {@link ToolWriteRecord}. See `derive-artifacts-from-chat-state`.
 *
 * Deriving from the persisted transcript, rather than tracking writes as they
 * happen, is what makes the list survive a reload: it is a pure function of
 * history that the live and replayed paths reproduce identically. This is also
 * why `session.workspace_file_changed` is unsuitable, since it exists only in
 * the CLI and is dropped by Mission Control's event allowlist, so it would
 * populate live and then vanish on reload.
 *
 * Only writes made through recognised write tools are visible, so shell
 * redirection, in-place editors, MCP server tools, and sub-agents that do not
 * surface their own tool calls are all missed. Detecting shell writes would mean
 * parsing an unbounded grammar, and a wrong path is worse than an omitted one.
 * The list is therefore high-precision but not exhaustive.
 */

import type {ContentRef, FileEdit} from '@microsoft/agent-host-protocol'

import {commonRootSegmentCount, languageForPath, splitSegments} from './artifact-paths'
import type {ArtifactOperation as ArtifactOperationValue} from './artifact-types'
import {previewForPath} from './preview-scope'
import {applyReplacements} from './replacements'
import {writesFor, type ToolWriteRecord, type Write} from './tool-writes'

export type ArtifactOperation = ArtifactOperationValue

export interface ArtifactContentRefOrigin {
  readonly environmentId: string
  readonly sessionId: string
}

export interface CollectArtifactsOptions {
  contentRefOrigin?: ArtifactContentRefOrigin
}

export interface Artifact {
  absolutePath: string
  /** Path trimmed of the root shared by all artifacts, for display. */
  displayPath: string
  fileName: string
  /** `displayPath` minus `fileName`, without a trailing separator. */
  directory: string
  /** `created` only when no earlier write to the same path was seen. */
  operation: ArtifactOperation
  /** Tool call id of the most recent successful write. */
  toolCallId: string
  preview?: string
  /**
   * Full file body, when a tool call carried one or an edit can reconstruct it.
   *
   * Populated by whole-file writes: `create`, `str_replace_editor` with
   * `command: 'create'`, and each `*** Add File:` block inside `apply_patch`.
   * Exact string replacements and diff hunks update a previously captured body.
   * Other partial edits leave this unset rather than showing stale content.
   */
  content?: string
  contentRef?: ContentRef
  readonly contentRefOrigin?: ArtifactContentRefOrigin
  language: string
}

function localFilePath(uri: string): string | undefined {
  // URL parsing turns a raw backslash in a file: URL into a path separator.
  if (uri.includes('\\')) return undefined
  try {
    const parsed = new URL(uri, 'https://github.com')
    if (
      parsed.protocol !== 'file:' ||
      (parsed.hostname !== '' && parsed.hostname !== 'localhost') ||
      parsed.username !== '' ||
      parsed.password !== '' ||
      parsed.port !== '' ||
      parsed.search !== '' ||
      parsed.hash !== ''
    ) {
      return undefined
    }
    const path = decodeURIComponent(parsed.pathname)
    return path.includes('\\') ? undefined : path
  } catch {
    return undefined
  }
}

function relativeWritePath(path: string): string | undefined {
  if (path.startsWith('/') || path.includes('\\')) return undefined
  const relativePath = path.startsWith('./') ? path.slice(2) : path
  const segments = relativePath.split('/')
  return segments.some(segment => segment === '' || segment === '.' || segment === '..') ? undefined : relativePath
}

function contentRefsForWrites(allWrites: readonly Write[], fileEdits: readonly FileEdit[]): Map<Write, ContentRef> {
  type Candidate = NonNullable<FileEdit['after']>

  // A patch can touch one path in several blocks, but its fileEdits describe
  // only the final content, so only the last write to each path can match.
  const writes = [...new Map(allWrites.map(write => [write.path, write])).values()]

  const candidatesByUri = new Map<string, Candidate>()
  for (const fileEdit of fileEdits) {
    if (fileEdit.after) candidatesByUri.set(fileEdit.after.uri, fileEdit.after)
  }

  const candidates = [...candidatesByUri.values()]
  const claimed = new Set<Candidate>()
  const matches = new Map<Write, ContentRef>()

  for (const write of writes) {
    if (!write.path.startsWith('/')) continue
    const candidate = candidates.find(value => !claimed.has(value) && localFilePath(value.uri) === write.path)
    if (!candidate) continue
    claimed.add(candidate)
    matches.set(write, candidate.content)
  }

  const suffixCandidates = new Map<Write, Candidate[]>()
  for (const write of writes) {
    if (matches.has(write)) continue
    const relativePath = relativeWritePath(write.path)
    if (relativePath === undefined) continue
    suffixCandidates.set(
      write,
      candidates.filter(
        candidate => !claimed.has(candidate) && localFilePath(candidate.uri)?.endsWith(`/${relativePath}`) === true,
      ),
    )
  }

  const candidateMatchCounts = new Map<Candidate, number>()
  for (const candidatesForWrite of suffixCandidates.values()) {
    for (const candidate of candidatesForWrite) {
      candidateMatchCounts.set(candidate, (candidateMatchCounts.get(candidate) ?? 0) + 1)
    }
  }

  for (const [write, candidatesForWrite] of suffixCandidates) {
    if (candidatesForWrite.length !== 1) continue
    const candidate = candidatesForWrite[0]
    if (!candidate || candidateMatchCounts.get(candidate) !== 1) continue
    claimed.add(candidate)
    matches.set(write, candidate.content)
  }

  // Pair a lone write and fileEdit only when their paths cannot be compared,
  // such as a Windows path or a non-file URI. Comparable paths that differ
  // describe different files.
  if (matches.size === 0 && writes.length === 1 && candidates.length === 1) {
    const write = writes[0]
    const candidate = candidates[0]
    const comparable =
      candidate !== undefined &&
      localFilePath(candidate.uri) !== undefined &&
      write !== undefined &&
      (write.path.startsWith('/') || relativeWritePath(write.path) !== undefined)
    if (write && candidate && !comparable) matches.set(write, candidate.content)
  }

  return matches
}

function replaceMapKeyPreservingOrder(
  map: Map<string, Artifact>,
  previousPath: string,
  path: string,
  artifact: Artifact,
) {
  if (previousPath === path || !map.has(previousPath)) {
    map.set(path, artifact)
    return
  }

  const entries = [...map.entries()]
  map.clear()
  for (const [existingPath, existingArtifact] of entries) {
    if (existingPath === previousPath) {
      map.set(path, artifact)
    } else if (existingPath !== path) {
      map.set(existingPath, existingArtifact)
    }
  }
}

/**
 * Reduce normalised tool calls to the files they wrote, in first-write order.
 *
 * Writes are deduplicated by absolute path keeping the most recent one, so a
 * file edited repeatedly appears once. A path first seen as `created` stays
 * `created` even after later edits. A deletion removes the path, and a later
 * re-create brings it back. Failed and in-flight calls are ignored.
 */
export function collectArtifacts(
  records: Iterable<ToolWriteRecord>,
  options: CollectArtifactsOptions = {},
): Artifact[] {
  // Insertion order is first-write order, which `Map` preserves across updates.
  const byPath = new Map<string, Artifact>()

  for (const record of records) {
    if (!record.succeeded) continue

    const writes = writesFor(record)
    const nonDeletedWrites = writes.filter(write => write.operation !== 'deleted')
    const contentRefs = contentRefsForWrites(nonDeletedWrites, record.fileEdits ?? [])

    for (const write of writes) {
      // A deletion removes the file, so drop any artifact already collected for
      // it. Handled inline (not as a post-hoc filter) so it stays ordered
      // against later writes: a re-create after a delete restores the artifact.
      if (write.operation === 'deleted') {
        byPath.delete(write.path)
        continue
      }
      const previousPath = write.previousPath ?? write.path
      const existing = byPath.get(previousPath)
      // The result covers the whole call, so keep only what describes this file.
      const scopedPreview = record.preview === undefined ? undefined : previewForPath(record.preview, write.path)
      let content = write.content
      if (content === undefined && existing?.content !== undefined && write.replacements !== undefined) {
        content = applyReplacements(existing.content, write.replacements)
      }
      const contentRef = contentRefs.get(write)
      const artifact: Artifact = {
        absolutePath: write.path,
        // Both are finalised below, once the whole set is known.
        displayPath: write.path,
        directory: '',
        fileName: splitSegments(write.path).at(-1) ?? write.path,
        operation: existing?.operation === 'created' ? 'created' : write.operation,
        toolCallId: record.toolCallId,
        language: languageForPath(write.path),
        ...(scopedPreview === undefined ? {} : {preview: scopedPreview}),
        ...(content === undefined ? {} : {content}),
        ...(contentRef === undefined ? {} : {contentRef}),
        ...(contentRef === undefined || options.contentRefOrigin === undefined
          ? {}
          : {contentRefOrigin: options.contentRefOrigin}),
      }
      replaceMapKeyPreservingOrder(byPath, previousPath, write.path, artifact)
    }
  }

  const artifacts = [...byPath.values()]
  const rootSegments = commonRootSegmentCount(artifacts.map(artifact => artifact.absolutePath))

  return artifacts.map(artifact => {
    const segments = splitSegments(artifact.absolutePath).slice(rootSegments)
    return {
      ...artifact,
      displayPath: segments.join('/') || artifact.fileName,
      directory: segments.slice(0, -1).join('/'),
    }
  })
}
